// =============================================================================
// AllocationForm.jsx - Allocation Creation Form Component
// =============================================================================
// Form for creating single or recurring pitch allocations
// Updated layout: Left column (Type, Team, Start Time, Duration) + Right column (Sections grid)
// Enhanced with searchable team dropdown for large team counts (50+)
// Added Save/Load buttons for allocation import/export (admin only)
// =============================================================================

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Hint } from './UnifiedPitchAllocator';

// =============================================================================
// SearchableTeamDropdown - Handles 50+ teams with search and filtering
// =============================================================================
const SearchableTeamDropdown = ({
  teams,
  selectedTeamId,
  onSelectTeam,
  disabled,
  hasError
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAgeGroup, setSelectedAgeGroup] = useState('All');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Extract unique age groups from teams
  const ageGroups = useMemo(() => {
    const groups = new Set();
    teams.forEach(team => {
      if (team.ageGroup) {
        groups.add(team.ageGroup);
      }
    });
    // Sort age groups naturally (U6, U7, ... U18, Seniors, Masters)
    const sortedGroups = Array.from(groups).sort((a, b) => {
      // Extract numbers for comparison
      const numA = parseInt(a.replace(/\D/g, '')) || 999;
      const numB = parseInt(b.replace(/\D/g, '')) || 999;
      if (numA !== numB) return numA - numB;
      return a.localeCompare(b);
    });
    return ['All', ...sortedGroups];
  }, [teams]);

  // Natural sort function for team names
  const naturalSort = (a, b) => {
    return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
  };

  // Filter and sort teams
  const filteredTeams = useMemo(() => {
    return teams
      .filter(team => {
        const matchesSearch = team.name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesAgeGroup = selectedAgeGroup === 'All' || team.ageGroup === selectedAgeGroup;
        return matchesSearch && matchesAgeGroup;
      })
      .sort(naturalSort);
  }, [teams, searchTerm, selectedAgeGroup]);

  // Group filtered teams by age group for display
  const groupedTeams = useMemo(() => {
    return filteredTeams.reduce((acc, team) => {
      const group = team.ageGroup || 'Other';
      if (!acc[group]) {
        acc[group] = [];
      }
      acc[group].push(team);
      return acc;
    }, {});
  }, [filteredTeams]);

  // Get selected team object
  const selectedTeam = teams.find(t => t.id === selectedTeamId);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle team selection
  const handleSelectTeam = (team) => {
    onSelectTeam(team.id);
    setIsOpen(false);
    setSearchTerm('');
  };

  // Get contrast color for team color swatch
  const getContrastColor = (hexColor) => {
    if (!hexColor || !hexColor.startsWith('#')) return '#000000';
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#000000' : '#ffffff';
  };

  return (
    <div style={{ position: 'relative' }} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        style={{
          width: '100%',
          maxWidth: '350px',
          padding: '10px 12px',
          paddingRight: '36px',
          border: hasError ? '1px solid #ef4444' : '1px solid #d1d5db',
          borderRadius: '6px',
          fontSize: '14px',
          backgroundColor: disabled ? '#f9fafb' : 'white',
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          textAlign: 'left',
          position: 'relative'
        }}
      >
        {selectedTeam ? (
          <>
            <span
              style={{
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                flexShrink: 0,
                backgroundColor: selectedTeam.color || '#9ca3af',
                border: '1px solid rgba(0,0,0,0.1)'
              }}
            />
            <span style={{ color: '#1f2937', flex: 1 }}>{selectedTeam.name}</span>
            {selectedTeam.ageGroup && (
              <span style={{ color: '#9ca3af', fontSize: '12px' }}>({selectedTeam.ageGroup})</span>
            )}
          </>
        ) : (
          <span style={{ color: '#9ca3af' }}>Select a team...</span>
        )}
        <svg
          style={{
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: `translateY(-50%) ${isOpen ? 'rotate(180deg)' : ''}`,
            width: '16px',
            height: '16px',
            color: '#9ca3af',
            transition: 'transform 0.15s ease'
          }}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div style={{
          position: 'absolute',
          zIndex: 100,
          width: '100%',
          maxWidth: '350px',
          marginTop: '4px',
          backgroundColor: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden'
        }}>
          {/* Search Input */}
          <div style={{
            padding: '12px',
            borderBottom: '1px solid #f3f4f6',
            backgroundColor: '#f9fafb'
          }}>
            <div style={{ position: 'relative' }}>
              <svg
                style={{
                  position: 'absolute',
                  left: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '16px',
                  height: '16px',
                  color: '#9ca3af'
                }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search teams..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 32px 8px 36px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    padding: '2px',
                    cursor: 'pointer',
                    color: '#9ca3af'
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Age Group Filter Pills */}
            {ageGroups.length > 2 && (
              <div style={{
                marginTop: '10px',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '6px'
              }}>
                {ageGroups.map(group => (
                  <button
                    key={group}
                    onClick={() => setSelectedAgeGroup(group)}
                    style={{
                      padding: '4px 10px',
                      fontSize: '12px',
                      borderRadius: '12px',
                      border: 'none',
                      cursor: 'pointer',
                      backgroundColor: selectedAgeGroup === group ? '#3b82f6' : '#e5e7eb',
                      color: selectedAgeGroup === group ? 'white' : '#4b5563',
                      fontWeight: selectedAgeGroup === group ? '500' : '400',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {group}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Team List */}
          <div style={{
            maxHeight: '280px',
            overflowY: 'auto'
          }}>
            {Object.keys(groupedTeams).length > 0 ? (
              // When filtering by specific age group, show flat list
              selectedAgeGroup !== 'All' ? (
                <div style={{ padding: '8px 0' }}>
                  {filteredTeams.map(team => (
                    <button
                      key={team.id}
                      onClick={() => handleSelectTeam(team)}
                      style={{
                        width: '100%',
                        padding: '10px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        backgroundColor: team.id === selectedTeamId ? '#eff6ff' : 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'background-color 0.15s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (team.id !== selectedTeamId) {
                          e.currentTarget.style.backgroundColor = '#f9fafb';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (team.id !== selectedTeamId) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <span
                        style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          flexShrink: 0,
                          backgroundColor: team.color || '#9ca3af',
                          border: '1px solid rgba(0,0,0,0.1)'
                        }}
                      />
                      <span style={{ 
                        flex: 1, 
                        color: '#1f2937',
                        fontWeight: team.id === selectedTeamId ? '500' : '400'
                      }}>
                        {team.name}
                      </span>
                      {team.id === selectedTeamId && (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              ) : (
                // Show grouped by age group when "All" is selected
                Object.entries(groupedTeams)
                  .sort(([a], [b]) => {
                    const numA = parseInt(a.replace(/\D/g, '')) || 999;
                    const numB = parseInt(b.replace(/\D/g, '')) || 999;
                    if (numA !== numB) return numA - numB;
                    return a.localeCompare(b);
                  })
                  .map(([group, groupTeams]) => (
                    <div key={group}>
                      <div style={{
                        padding: '8px 16px',
                        fontSize: '11px',
                        fontWeight: '600',
                        color: '#6b7280',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        backgroundColor: '#f9fafb',
                        borderTop: '1px solid #f3f4f6',
                        borderBottom: '1px solid #f3f4f6',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1
                      }}>
                        {group}
                      </div>
                      {groupTeams.map(team => (
                        <button
                          key={team.id}
                          onClick={() => handleSelectTeam(team)}
                          style={{
                            width: '100%',
                            padding: '10px 16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            backgroundColor: team.id === selectedTeamId ? '#eff6ff' : 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            textAlign: 'left',
                            transition: 'background-color 0.15s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (team.id !== selectedTeamId) {
                              e.currentTarget.style.backgroundColor = '#f9fafb';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (team.id !== selectedTeamId) {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }
                          }}
                        >
                          <span
                            style={{
                              width: '20px',
                              height: '20px',
                              borderRadius: '50%',
                              flexShrink: 0,
                              backgroundColor: team.color || '#9ca3af',
                              border: '1px solid rgba(0,0,0,0.1)'
                            }}
                          />
                          <span style={{ 
                            flex: 1, 
                            color: '#1f2937',
                            fontWeight: team.id === selectedTeamId ? '500' : '400'
                          }}>
                            {team.name}
                          </span>
                          {team.id === selectedTeamId && (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </button>
                      ))}
                    </div>
                  ))
              )
            ) : (
              <div style={{
                padding: '32px 16px',
                textAlign: 'center'
              }}>
                <svg
                  style={{ width: '40px', height: '40px', color: '#d1d5db', margin: '0 auto 12px' }}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>No teams found</p>
                <p style={{ fontSize: '12px', color: '#9ca3af', margin: '4px 0 0' }}>
                  Try a different search or filter
                </p>
              </div>
            )}
          </div>

          {/* Clear Selection Footer */}
          {selectedTeamId && (
            <div style={{
              padding: '8px 12px',
              borderTop: '1px solid #f3f4f6',
              backgroundColor: '#f9fafb'
            }}>
              <button
                type="button"
                onClick={() => {
                  onSelectTeam('');
                  setIsOpen(false);
                }}
                style={{
                  width: '100%',
                  padding: '6px',
                  fontSize: '12px',
                  color: '#6b7280',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#fee2e2';
                  e.currentTarget.style.color = '#dc2626';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#6b7280';
                }}
              >
                Clear selection
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// AllocationForm - Main Component
// =============================================================================
const AllocationForm = ({
  teams,
  timeSlots,
  durationOptions,
  currentSections,
  formData,
  onFormChange,
  onSubmit,
  onClearAll,
  onSelectAllSections,
  onClearAllSections,
  onToggleSection,
  getSectionOptions,
  getMatchDayDuration,
  showGrassArea,
  normalizedPitchId,
  isSaving,
  isDeleting,
  isProcessingRecurring,
  successMessage,
  errorMessage,
  attemptedSubmit,
  hasConflict,
  recurringDates,
  recurringConflicts,
  hintRefresh,
  // New props for Save/Load functionality
  onSave,
  onLoad,
  hasAllocations = false,
  isAdmin = false
}) => {
  
  const {
    team,
    slot,
    allocationType,
    duration,
    selectedSections,
    sectionGroup,
    isRecurring,
    recurringEndDate,
    date
  } = formData;
  
  // Get selected team object
  const selectedTeam = teams.find(t => t.id === team);
  
  // Check if team can make match allocations based on pitch configuration
  const canMakeMatchAllocation = () => {
    if (!selectedTeam) return true;
    const teamMatchSections = selectedTeam.matchPitchAreaRequired || 2;
    return teamMatchSections <= currentSections.length;
  };
  
  const canMakeMatch = canMakeMatchAllocation();
  const isMatchTypeDisabled = !canMakeMatch;
  
  // Auto-switch to training if team can't make matches
  useEffect(() => {
    if (team && allocationType === 'match' && !canMakeMatch) {
      onFormChange('allocationType', 'training');
    }
  }, [team, allocationType, canMakeMatch, onFormChange]);
  
  // Determine if form should be disabled
  const isFormDisabled = isSaving || isDeleting || isProcessingRecurring;
  
  // Get section options for match mode
  // These define the pitch layouts based on team requirements
  const getMatchSectionOptions = () => {
    // Call parent's getSectionOptions with the selected team
    if (getSectionOptions && team) {
      const options = getSectionOptions(team);
      if (options && options.length > 0) return options;
    }
    
    // If no team selected yet, show a placeholder
    if (!team) {
      return [{ value: '', label: 'Select a team first...' }];
    }
    
    // Fallback section options based on available sections
    const numSections = currentSections.length;
    const options = [];
    
    // Small (1 section) - individual sections
    if (numSections >= 1) {
      currentSections.forEach(section => {
        options.push({ value: section, label: `Small - ${section}` });
      });
    }
    
    // Quarter (2 sections) - paired sections
    if (numSections >= 4) {
      options.push({ value: 'AC', label: 'Quarter - A & C' });
      options.push({ value: 'BD', label: 'Quarter - B & D' });
    }
    if (numSections >= 6) {
      options.push({ value: 'EG', label: 'Quarter - E & G' });
      options.push({ value: 'FH', label: 'Quarter - F & H' });
    }
    
    // Half (4 sections)
    if (numSections >= 8) {
      options.push({ value: 'ABCD', label: 'Half - A-D' });
      options.push({ value: 'EFGH', label: 'Half - E-H' });
    }
    
    // Full (8 sections)
    if (numSections === 8) {
      options.push({ value: 'ABCDEFGH', label: 'Full Pitch' });
    }
    
    return options;
  };

  // State for warning dismissal
  const [warningDismissed, setWarningDismissed] = useState(false);
  const [interactionCount, setInteractionCount] = useState(0);

  // Reset warning when team changes
  useEffect(() => {
    setWarningDismissed(false);
    setInteractionCount(0);
  }, [team]);

  // Track interaction for warning
  const handleInteraction = () => {
    if (isMatchTypeDisabled && !warningDismissed) {
      setInteractionCount(prev => prev + 1);
    }
  };

  // Handle team selection from searchable dropdown
  const handleTeamSelect = (teamId) => {
    onFormChange('team', teamId);
    handleInteraction();
  };

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      marginBottom: '24px',
      position: 'relative'
    }}>
      {/* Processing Overlay */}
      {isProcessingRecurring && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
          borderRadius: '12px'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #e5e7eb',
            borderTopColor: '#3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{ marginTop: '16px', color: '#4b5563', fontWeight: '500' }}>
            Processing recurring allocation...
          </p>
        </div>
      )}

      {/* Title */}
      <h2 style={{
        fontSize: '18px',
        fontWeight: '600',
        color: '#1f2937',
        marginBottom: '24px',
        textAlign: 'center'
      }}>
        Add New Allocation
      </h2>

      {/* Main Form Layout - Two Columns Centered */}
      <div style={{
        display: 'flex',
        gap: '80px',
        marginBottom: '20px',
        justifyContent: 'center'
      }}>
        {/* Left Column - Form Fields */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          width: '220px'
        }}>
          {/* Type */}
          <div style={{ position: 'relative' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '6px'
            }}>
              Type
            </label>
            <select
              value={allocationType}
              onChange={(e) => {
                onFormChange('allocationType', e.target.value);
                handleInteraction();
              }}
              disabled={isFormDisabled}
              style={{
                width: '200px',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: isFormDisabled ? '#f9fafb' : 'white',
                cursor: isFormDisabled ? 'not-allowed' : 'pointer'
              }}
            >
              <option value="training">Training</option>
              <option 
                value="match" 
                disabled={isMatchTypeDisabled}
                style={{ color: isMatchTypeDisabled ? '#9ca3af' : 'inherit' }}
              >
                Match {isMatchTypeDisabled ? '(Not available on this pitch)' : ''}
              </option>
            </select>
            <Hint
              hintKey="hint_session_type"
              message="Choose the session type: Training or Match."
              position="right"
              refreshTrigger={hintRefresh}
            />
          </div>

          {/* Team - Now using SearchableTeamDropdown */}
          <div style={{ position: 'relative' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '6px'
            }}>
              Team
              {teams.length > 20 && (
                <span style={{ 
                  fontSize: '11px', 
                  color: '#9ca3af', 
                  fontWeight: '400',
                  marginLeft: '8px'
                }}>
                  ({teams.length} teams)
                </span>
              )}
            </label>
            <SearchableTeamDropdown
              teams={teams}
              selectedTeamId={team}
              onSelectTeam={handleTeamSelect}
              disabled={isFormDisabled}
              hasError={attemptedSubmit && !team}
            />
            <Hint
              hintKey="hint_team"
              message="Select the team for this allocation."
              position="right"
              refreshTrigger={hintRefresh}
            />
          </div>

          {/* Start Time */}
          <div style={{ position: 'relative' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '6px'
            }}>
              Start Time
            </label>
            <select
              value={slot}
              onChange={(e) => {
                onFormChange('slot', e.target.value);
                handleInteraction();
              }}
              disabled={isFormDisabled}
              style={{
                width: '200px',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: isFormDisabled ? '#f9fafb' : 'white',
                cursor: isFormDisabled ? 'not-allowed' : 'pointer'
              }}
            >
              {timeSlots.map(time => (
                <option key={time} value={time}>{time}</option>
              ))}
            </select>
            <Hint
              hintKey="hint_start_time"
              message="Select the start time for the session."
              position="right"
              refreshTrigger={hintRefresh}
            />
          </div>

          {/* Duration */}
          <div style={{ position: 'relative' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '6px'
            }}>
              Duration
            </label>
            <select
              value={duration}
              onChange={(e) => {
                onFormChange('duration', e.target.value);
                handleInteraction();
              }}
              disabled={isFormDisabled || allocationType === 'match'}
              style={{
                width: '200px',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: (isFormDisabled || allocationType === 'match') ? '#f9fafb' : 'white',
                cursor: (isFormDisabled || allocationType === 'match') ? 'not-allowed' : 'pointer'
              }}
            >
              {durationOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {allocationType === 'match' && (
              <p style={{
                fontSize: '11px',
                color: '#6b7280',
                marginTop: '4px',
                fontStyle: 'italic'
              }}>
                Duration auto-set for matches
              </p>
            )}
            <Hint
              hintKey="hint_duration"
              message="Select how long the session will last."
              position="right"
              refreshTrigger={hintRefresh}
            />
          </div>
        </div>

        {/* Right Column - Sections */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          width: '200px'
        }}>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            color: '#374151',
            marginBottom: '2px'
          }}>
            Sections
          </label>
          
          {allocationType === 'training' ? (
            <>
              {/* Training Mode - Section Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '8px',
                marginBottom: '8px'
              }}>
                {currentSections.map((section) => (
                  <button
                    key={section}
                    type="button"
                    onClick={() => {
                      onToggleSection(section);
                      handleInteraction();
                    }}
                    disabled={isFormDisabled}
                    style={{
                      padding: '8px 16px',
                      fontSize: '14px',
                      fontWeight: '500',
                      border: selectedSections.includes(section)
                        ? '2px solid #374151'
                        : '1px solid #d1d5db',
                      borderRadius: '6px',
                      backgroundColor: selectedSections.includes(section)
                        ? '#f3f4f6'
                        : 'white',
                      color: selectedSections.includes(section)
                        ? '#374151'
                        : '#6b7280',
                      cursor: isFormDisabled ? 'not-allowed' : 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {section}
                  </button>
                ))}
              </div>

              {/* Select All / Clear All Buttons */}
              <div style={{
                display: 'flex',
                gap: '8px'
              }}>
                <button
                  type="button"
                  onClick={() => {
                    onSelectAllSections();
                    handleInteraction();
                  }}
                  disabled={isFormDisabled}
                  style={{
                    flex: 1,
                    padding: '6px 12px',
                    fontSize: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    color: '#6b7280',
                    cursor: isFormDisabled ? 'not-allowed' : 'pointer'
                  }}
                >
                  Select All
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onClearAllSections();
                    handleInteraction();
                  }}
                  disabled={isFormDisabled}
                  style={{
                    flex: 1,
                    padding: '6px 12px',
                    fontSize: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    color: '#6b7280',
                    cursor: isFormDisabled ? 'not-allowed' : 'pointer'
                  }}
                >
                  Clear All
                </button>
              </div>

              <p style={{
                fontSize: '12px',
                color: '#6b7280',
                marginTop: '4px'
              }}>
                Select one or more sections for training
              </p>
            </>
          ) : (
            <>
              {/* Match Mode - Section Group Dropdown */}
              <select
                value={sectionGroup}
                onChange={(e) => {
                  onFormChange('sectionGroup', e.target.value);
                  handleInteraction();
                }}
                disabled={isFormDisabled || !team}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: (isFormDisabled || !team) ? '#f9fafb' : 'white',
                  cursor: (isFormDisabled || !team) ? 'not-allowed' : 'pointer'
                }}
              >
                {getMatchSectionOptions().map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>

              <p style={{
                fontSize: '12px',
                color: '#6b7280',
                marginTop: '4px'
              }}>
                Select pitch area for match
              </p>
              
              {/* Show selected sections preview */}
              {sectionGroup && team && (
                <div style={{
                  marginTop: '8px',
                  padding: '8px 12px',
                  backgroundColor: '#f3f4f6',
                  borderRadius: '6px',
                  fontSize: '12px',
                  color: '#4b5563'
                }}>
                  <strong>Selected:</strong> {sectionGroup.split('').join(', ')}
                </div>
              )}
            </>
          )}

          {/* Recurring Allocation Checkbox */}
          <div style={{ marginTop: '16px' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: isFormDisabled ? 'not-allowed' : 'pointer'
            }}>
              <input
                type="checkbox"
                checked={isRecurring}
                onChange={(e) => {
                  onFormChange('isRecurring', e.target.checked);
                  handleInteraction();
                }}
                disabled={isFormDisabled}
                style={{
                  width: '16px',
                  height: '16px',
                  cursor: isFormDisabled ? 'not-allowed' : 'pointer'
                }}
              />
              <span style={{
                fontSize: '14px',
                color: '#374151'
              }}>
                Recurring Allocation
              </span>
            </label>
          </div>

          {/* Recurring End Date Selector */}
          {isRecurring && (
            <div style={{ marginTop: '12px' }}>
              <label style={{
                display: 'block',
                fontSize: '12px',
                fontWeight: '600',
                color: '#1e40af',
                marginBottom: '6px'
              }}>
                📅 Repeat Until
              </label>
              <input
                type="date"
                value={recurringEndDate}
                onChange={(e) => {
                  onFormChange('recurringEndDate', e.target.value);
                  handleInteraction();
                }}
                min={date}
                disabled={isFormDisabled}
                style={{
                  width: '100%',
                  maxWidth: '250px',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: isFormDisabled ? '#f9fafb' : 'white',
                  cursor: isFormDisabled ? 'not-allowed' : 'pointer'
                }}
              />
              
              {/* Helper text */}
              <div style={{
                marginTop: '6px',
                fontSize: '12px',
                color: '#6b7280'
              }}>
                {recurringEndDate ? (
                  <>
                    Allocations will repeat weekly until{' '}
                    <strong style={{ color: '#3b82f6' }}>
                      {new Date(recurringEndDate + 'T00:00:00').toLocaleDateString('en-AU', {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </strong>
                  </>
                ) : (
                  'Select when recurring allocations should stop'
                )}
              </div>

              {/* Recurring Preview */}
              {recurringDates && recurringDates.length > 0 && (
                <div style={{
                  marginTop: '12px',
                  padding: '12px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb',
                  maxHeight: '150px',
                  overflowY: 'auto'
                }}>
                  <p style={{
                    fontSize: '12px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Will create {recurringDates.length} allocation{recurringDates.length !== 1 ? 's' : ''}:
                  </p>
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '4px'
                  }}>
                    {recurringDates.map((date, idx) => {
                      const hasConflict = recurringConflicts && recurringConflicts[date];
                      return (
                        <span
                          key={idx}
                          style={{
                            fontSize: '11px',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            backgroundColor: hasConflict ? '#fef2f2' : '#ecfdf5',
                            color: hasConflict ? '#991b1b' : '#166534',
                            border: `1px solid ${hasConflict ? '#fecaca' : '#bbf7d0'}`
                          }}
                        >
                          {date} {hasConflict && '⚠️'}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Match Type Warning - Only show when relevant */}
      {isMatchTypeDisabled && team && !warningDismissed && (
        <div style={{
          marginBottom: '16px',
          padding: '12px',
          backgroundColor: '#fffbeb',
          border: '1px solid #fcd34d',
          borderRadius: '6px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '16px' }}>⚠️</span>
            <div>
              <p style={{
                fontSize: '13px',
                fontWeight: '500',
                color: '#92400e',
                margin: 0
              }}>
                Match allocations not available
              </p>
              <p style={{
                fontSize: '12px',
                color: '#a16207',
                margin: '2px 0 0 0'
              }}>
                This team requires more sections than available on this pitch. Training allocations are still available.
              </p>
            </div>
          </div>
          <button
            onClick={() => setWarningDismissed(true)}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '18px',
              color: '#92400e',
              cursor: 'pointer',
              padding: '4px'
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Divider */}
      <div style={{
        borderTop: '1px solid #e5e7eb',
        margin: '20px 0'
      }} />

      {/* Action Buttons */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '12px',
        position: 'relative'
      }}>
        {/* Left side - Save/Load buttons (Admin only) */}
        {isAdmin ? (
          <div style={{
            display: 'flex',
            gap: '8px'
          }}>
            <button
              onClick={onLoad}
              disabled={isFormDisabled || !onLoad}
              style={{
                padding: '10px 16px',
                fontSize: '14px',
                fontWeight: '500',
                color: (isFormDisabled || !onLoad) ? '#9ca3af' : '#374151',
                backgroundColor: (isFormDisabled || !onLoad) ? '#f3f4f6' : 'white',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                cursor: (isFormDisabled || !onLoad) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={(e) => {
                if (!isFormDisabled && onLoad) {
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                  e.currentTarget.style.borderColor = '#9ca3af';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = (!isFormDisabled && onLoad) ? 'white' : '#f3f4f6';
                e.currentTarget.style.borderColor = '#d1d5db';
              }}
            >
              <span>📥</span>
              Load
            </button>
            <button
              onClick={onSave}
              disabled={isFormDisabled || !hasAllocations || !onSave}
              style={{
                padding: '10px 16px',
                fontSize: '14px',
                fontWeight: '500',
                color: (isFormDisabled || !hasAllocations || !onSave) ? '#9ca3af' : '#374151',
                backgroundColor: (isFormDisabled || !hasAllocations || !onSave) ? '#f3f4f6' : 'white',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                cursor: (isFormDisabled || !hasAllocations || !onSave) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={(e) => {
                if (!isFormDisabled && hasAllocations && onSave) {
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                  e.currentTarget.style.borderColor = '#9ca3af';
                }
              }}
              onMouseLeave={(e) => {
                if (!isFormDisabled && hasAllocations && onSave) {
                  e.currentTarget.style.backgroundColor = 'white';
                }
                e.currentTarget.style.borderColor = '#d1d5db';
              }}
            >
              <span>📤</span>
              Save
            </button>
          </div>
        ) : (
          <div /> /* Empty spacer when not admin */
        )}

        {/* Right side - Add/Clear buttons */}
        <div style={{
          display: 'flex',
          gap: '12px'
        }}>
          <div style={{ position: 'relative' }}>
            <Hint
              hintKey="hint_add_button"
              message="Click to save your booking to the pitch."
              position="top"
              refreshTrigger={hintRefresh}
            />
            <button
              onClick={onSubmit}
              disabled={isFormDisabled || !team || (allocationType === 'training' && selectedSections.length === 0)}
              style={{
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: '500',
                color: 'white',
                backgroundColor: (isFormDisabled || !team || (allocationType === 'training' && selectedSections.length === 0)) 
                  ? '#9ca3af' 
                  : '#374151',
                border: 'none',
                borderRadius: '6px',
                cursor: (isFormDisabled || !team || (allocationType === 'training' && selectedSections.length === 0)) 
                  ? 'not-allowed' 
                  : 'pointer'
              }}
            >
              {isSaving ? 'Adding...' : `Add ${allocationType === 'training' ? 'Training' : 'Match'}`}
            </button>
          </div>
          <button
            onClick={onClearAll}
            disabled={isDeleting}
            style={{
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#dc2626',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              cursor: isDeleting ? 'not-allowed' : 'pointer',
              opacity: isDeleting ? 0.5 : 1
            }}
          >
            {isDeleting ? 'Clearing...' : 'Clear All'}
          </button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div style={{
          marginTop: '16px',
          padding: '12px',
          backgroundColor: '#ecfdf5',
          border: '1px solid #a7f3d0',
          borderRadius: '6px',
          color: '#065f46',
          fontSize: '14px'
        }}>
          {successMessage}
        </div>
      )}
      
      {errorMessage && (
        <div style={{
          marginTop: '16px',
          padding: '12px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '6px',
          color: '#991b1b',
          fontSize: '14px'
        }}>
          {errorMessage}
        </div>
      )}

      {/* Conflict Warning */}
      {hasConflict && (
        <div style={{
          marginTop: '16px',
          padding: '12px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '6px',
          color: '#991b1b',
          fontSize: '14px'
        }}>
          {isRecurring 
            ? '⚠️ Some dates have conflicts and will be skipped' 
            : '⚠️ This time slot conflicts with an existing allocation'}
        </div>
      )}

      {/* Spin animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AllocationForm;
